import depsloader from "../../../depsloader";

const GEOCODER_SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const GEOCODER_REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

// Maps-Key wird serverseitig ueber den Screenwork-Maps-Wrapper geladen und zur
// Laufzeit in das html-dataset gereicht. Nie statisch im Frontend hinterlegen.
function getMapsKey() {
  try {
    const el = document.querySelector("html");
    const key = el && el.dataset ? el.dataset.mapsGeocoderKey : "";
    return typeof key === "string" ? key.trim() : "";
  } catch (e) {
    return "";
  }
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    const geoaddress = {};

    const exists = (arr, obj) => {
      for (let i = 0; i < arr.length; i++) {
        if (!(arr[i] in obj)) {
          return false;
        }
        if (!obj[arr[i]]) {
          return false;
        }
      }

      return true;
    };

    // Neuer Geocoder liefert properties.type; Strassentreffer daraus ableiten.
    if (l.properties.type === "street" && l.properties.name && !l.properties.street) {
      l.properties.street = l.properties.name;
      l.properties.name = null;
    }

    // Legacy-Fallback: alter Adapter kennzeichnete Strassen ueber osm_key.
    if (l.properties.osm_key == "highway") {
      l.properties.street = l.properties.name;
      l.properties.name = null;
    }

    if (exists(["street", "housenumber"], l.properties)) {
      geoaddress["street"] = l.properties.street;
      geoaddress["number"] = l.properties.housenumber;
    } else if (exists(["street"], l.properties)) {
      geoaddress["street"] = l.properties.street;
    }

    if (exists(["postcode", "city", "name"], l.properties)) {
      geoaddress["zip"] = l.properties.postcode;
      if (l.properties.city == l.properties.name) {
        geoaddress["city"] = l.properties.city;
      } else {
        geoaddress["city"] = l.properties.city;

        let district = false;
        if (l.properties.type === "district") {
          district = true;
        } else if (l.properties.osm_key == "boundary") {
          district = true;
        } else if (l.properties.osm_key == "place" && l.properties.osm_value == "suburb") {
          district = true;
        }

        if (district) {
          geoaddress["district"] = l.properties.name;
        }
      }
    } else if (exists(["postcode", "city"], l.properties)) {
      geoaddress["zip"] = l.properties.postcode;
      geoaddress["city"] = l.properties.city;
    } else if (exists(["postcode", "name"], l.properties)) {
      geoaddress["zip"] = l.properties.postcode;
      geoaddress["city"] = l.properties.name;
    } else {
      geoaddress["city"] = l.properties.name;
    }

    // properties.district hat Vorrang und ist unabhaengig vom osm_key-Fallback.
    if (!geoaddress["district"] && l.properties.district) {
      geoaddress["district"] = l.properties.district;
    }

    geoaddress["lat"] = l.geometry.coordinates[1];
    geoaddress["lng"] = l.geometry.coordinates[0];

    geoaddress["state"] = l.properties.state;
    geoaddress["country"] = l.properties.country;

    geoaddress["text"] = this._geoaddressText(geoaddress);

    return geoaddress;
  }

  geocode(params) {
    const key = getMapsKey();
    if (!key) {
      // Ohne Key kontrolliert ohne Anfrage enden.
      this.axiosRequest = null;
      return Promise.resolve([]);
    }
    params.set("key", key);

    const CancelToken = this.axios.CancelToken;
    const source = CancelToken.source();
    this.axiosRequest = { source };

    return new Promise((resolve) => {
      this.axios.get(GEOCODER_SEARCH_URL, {
        cancelToken: source.token,
        timeout: REQUEST_TIMEOUT,
        params
      }).then(res => {
        const results = [];

        if (res.data && res.data.features) {
          res.data.features.forEach(l => {
            const geoaddress = this._geoaddress(l);
            if (geoaddress) {
              results.push(geoaddress);
            }
          });
        }

        resolve(results);
      }).catch((thrown) => {
        // Abbruch und Fehler settlen das Promise leer, damit keine haengenden
        // Promises entstehen. Aktualitaet sichert der Aufrufer ueber die Sequenz.
        if (!this.axios.isCancel(thrown)) {
          console.log(thrown);
        }
        resolve([]);
      }).finally(() => {
        if (this.axiosRequest && this.axiosRequest.source === source) {
          this.axiosRequest = null;
        }
      });
    });
  }

  autocomplete(q) {
    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("layer", "city");
    params.append("layer", "district");

    return this.geocode(params);
  }

  reverse(lat, lng) {
    const key = getMapsKey();
    if (!key) {
      // Ohne Key kontrolliert ohne Anfrage enden.
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      this.axios.get(GEOCODER_REVERSE_URL, {
        timeout: REQUEST_TIMEOUT,
        params: {
          lat: lat,
          lon: lng,
          lang: "de",
          key: key
        }
      }).then(res => {
        if (res.data && res.data.features && res.data.features.length > 0) {
          resolve(this._geoaddress(res.data.features[0]));
        } else {
          resolve(null);
        }
      }).catch(() => {
        resolve(null);
      });
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
